import { inject, Injectable } from '@angular/core';
import { ZoneStore } from '../infrastructure/zones/zone.store';
import { asArraySafe, asNumberSafe } from '@pf/shared-utility';
import { ZoneDto, ZoneRead } from '../entities/zone.dto';
import { SlotStore } from '../infrastructure/slots/slot.store';
import { SlotRead } from '../entities/slot.dto';

@Injectable()
export class ZoneSlotService {
    private _slotStore = inject(SlotStore);
    private _zoneStore = inject(ZoneStore);

    getNextPriority(zoneId: string): number {
        const zoneSlots = this.getZoneSlots(zoneId);

        if (!zoneSlots?.length) {
            return 1;
        }

        return (
            asNumberSafe(
                zoneSlots.sort(
                    (a, b) =>
                        asNumberSafe(b.slotPriority) -
                        asNumberSafe(a.slotPriority)
                )[0].slotPriority
            ) + 1
        );
    }

    private getZoneSlots(zoneId: string) {
        return asArraySafe(
            this._slotStore.filter((s: SlotRead) => s.zone?.id === zoneId)
        );
    }

    getZoneBarcode(zone: ZoneRead | ZoneDto): string {
        const barcode = asArraySafe(zone.locationIdentifiers)
            .sort((a, b) => asNumberSafe(a.level) - asNumberSafe(b.level))
            .map(l => l.name.padStart(3, '0'))
            .join('-');
        return `Z-${barcode}`;
    }

    getZoneSlotBarcode(
        zoneOrId?: string | ZoneRead,
        slotBarcodeSuffix?: string
    ): string {
        if (!zoneOrId) {
            return '';
        }

        const zone = this.getZone(zoneOrId);
        const zoneBarcode = this.getZoneBarcode(zone);
        const slotIdentifier =
            slotBarcodeSuffix || this.getNextBarcodeSuffix(zone);
        return this.formatSlotBarcode(zoneBarcode, slotIdentifier);
    }

    getZoneSlotBarcodeSuffix(
        zoneOrId?: string | ZoneRead,
        barcode?: string | null
    ): string {
        if (!zoneOrId) {
            return '';
        }

        const zone = this.getZone(zoneOrId);

        if (!barcode) {
            return this.getNextBarcodeSuffix(zone);
        }

        const barcodeParts = barcode.split('-');

        if (barcodeParts.length < 2) {
            return '';
        }
        return barcodeParts[1];
    }

    getBulkBarcodes(zoneId: string, quantity: number): string[] {
        const zone = this._zoneStore.get(zoneId);

        if (!zone) {
            return [];
        }

        const zonePrefix = this.getZoneBarcode(zone);
        const highestExistingSuffix = this.getHighestBarcodeSuffixNumber(zone);

        return Array.from({ length: quantity }, (_, index) => {
            return this.formatSlotBarcode(
                zonePrefix,
                this.getBarcodeSuffixString(highestExistingSuffix + index + 1)
            );
        });
    }

    private getZone(zoneOrId: string | ZoneRead) {
        return typeof zoneOrId === 'string'
            ? this._zoneStore.get(zoneOrId)
            : zoneOrId;
    }

    private getBarcodeSuffixString(val: number): string {
        return val.toString().padStart(3, '0');
    }

    private getHighestBarcodeSuffixNumber(zone: ZoneRead): number {
        const zoneSlots = this.getZoneSlots(zone.id);

        if (!zoneSlots?.length) {
            return 0;
        }

        const slot = zoneSlots.sort((a, b) =>
            (b.barcode || '').localeCompare(a.barcode || '')
        )[0];
        const barcode = this.getZoneSlotBarcodeSuffix(zone, slot.barcode);
        return parseInt(barcode, 10);
    }

    private getNextBarcodeSuffix(zone: ZoneRead): string {
        return this.getBarcodeSuffixString(
            this.getHighestBarcodeSuffixNumber(zone) + 1
        );
    }

    private formatSlotBarcode(
        zoneBarcode: string,
        slotIdentifier: string
    ): string {
        return `S-${slotIdentifier}-${zoneBarcode}`
            .replace(/[^a-zA-Z0-9-]/g, '')
            .toUpperCase();
    }
}
